import PageWrapper from 'common/components/PageWrapper'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { useMutateUpdateInventorySettings } from 'modules/inventory/api/mutation'
import { useInventorySettings } from 'modules/inventory/api/queries'
import { GreenSwitch } from 'modules/inventory/components/IsActiveSwitch'
import { usePermitToActionFeature } from 'modules/user/api/selector'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { PATH_DASHBOARD } from 'routes/paths'

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { Box, Chip, CircularProgress, Divider, IconButton, Stack, Typography } from '@mui/material'

const ENFORCE_STOCK_CODE = 'enforce_stock'

interface MockItem {
  name: string
  unit: string
  lot: string | null
  remaining: number
}

type Translate = (key: string, options?: Record<string, string>) => string

const getFoodPreviewItems = (t: Translate): MockItem[] => {
  const kg = t('settings.preview.unit.kg')
  return [
    { name: t('settings.preview.food_item', { name: 'A' }), unit: kg, lot: 'AB1106', remaining: 2000 },
    { name: t('settings.preview.food_item', { name: 'B' }), unit: kg, lot: 'GB2402', remaining: 32 },
    { name: t('settings.preview.food_item', { name: 'C' }), unit: kg, lot: 'CL1180', remaining: 0 },
  ]
}

const getMedicinePreviewItems = (t: Translate): MockItem[] => {
  const liter = t('settings.preview.unit.liter')
  const sachet = t('settings.preview.unit.sachet')
  const ton = t('settings.preview.unit.ton')
  return [
    { name: t('settings.preview.medicine_item', { name: 'A', unit: liter }), unit: liter, lot: '01', remaining: 180 },
    { name: t('settings.preview.medicine_item', { name: 'B', unit: sachet }), unit: sachet, lot: 'RB2210', remaining: 24 },
    { name: t('settings.preview.medicine_item', { name: 'C', unit: ton }), unit: ton, lot: 'CN0925', remaining: 0 },
  ]
}

const InventoryStockEnforcementPage = () => {
  const { t } = useTranslation('warehouse')
  const navigate = useNavigate()
  const canManage = usePermitToActionFeature([CRUDAction.Update], FeatureKey.Inventory)

  const { data, isLoading } = useInventorySettings()
  const { mutate, isLoading: isSaving } = useMutateUpdateInventorySettings()

  const [saving, setSaving] = useState(false)

  const foodGroup = data?.find((g) => g.feature_code === 'food')
  const medicineGroup = data?.find((g) => g.feature_code === 'medicine')
  const foodEnabled =
    foodGroup?.settings.find((s) => s.code === ENFORCE_STOCK_CODE)?.is_enabled ?? false
  const medicineEnabled =
    medicineGroup?.settings.find((s) => s.code === ENFORCE_STOCK_CODE)?.is_enabled ?? false
  // ค่าจริงของ food/medicine ควรตรงกันเสมอเพราะเราส่ง PATCH คู่กันทุกครั้ง (ดู handleToggle)
  const enforceStock = foodEnabled && medicineEnabled

  const LINKS = [
    { name: t('inventory.title'), href: PATH_DASHBOARD.inventory.product },
    { name: t('inventory.settings.title'), href: PATH_DASHBOARD.inventory.settings },
    { name: t('settings.stock_enforcement.title') },
  ]

  const handleToggle = (isEnabled: boolean) => {
    if (saving) return
    setSaving(true)
    mutate(
      {
        settings: [
          { feature_code: 'food', code: ENFORCE_STOCK_CODE, is_enabled: isEnabled },
          { feature_code: 'medicine', code: ENFORCE_STOCK_CODE, is_enabled: isEnabled },
        ],
      },
      {
        onSettled: () => setSaving(false),
      }
    )
  }

  return (
    <PageWrapper
      title={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={() => navigate(PATH_DASHBOARD.inventory.settings)}>
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="h4">{t('settings.stock_enforcement.title')}</Typography>
        </Stack>
      }
      titleHead={t('settings.stock_enforcement.title')}
      links={LINKS}
    >
      {isLoading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (
        <Box sx={{ maxWidth: 500 }}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: '0px 2px 8px 0px #00000014',
              p: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t('settings.stock_enforcement.label')}
              </Typography>
              <GreenSwitch
                checked={enforceStock}
                disabled={!canManage || saving || isSaving}
                onChange={(_, checked) => handleToggle(checked)}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {enforceStock
                ? t('settings.stock_enforcement.subtitle_on')
                : t('settings.stock_enforcement.subtitle_off')}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                bgcolor: 'warning.lighter',
                border: '1px solid',
                borderColor: 'warning.light',
                borderRadius: 2,
                p: 2,
              }}
            >
              <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: 'warning.dark', mt: 0.2 }} />
              <Typography variant="body2" color="warning.dark">
                {t('settings.stock_enforcement.warning')}
              </Typography>
            </Box>
          </Box>

          <PreviewSection
            title={t('settings.stock_enforcement.preview_food')}
            items={getFoodPreviewItems(t)}
            enforceStock={enforceStock}
          />
          <PreviewSection
            title={t('settings.stock_enforcement.preview_medicine')}
            items={getMedicinePreviewItems(t)}
            enforceStock={enforceStock}
          />
        </Box>
      )}
    </PageWrapper>
  )
}

interface PreviewSectionProps {
  title: string
  items: MockItem[]
  enforceStock: boolean
}

const PreviewSection = ({ title, items, enforceStock }: PreviewSectionProps) => {
  const { t } = useTranslation('warehouse')

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {title}
      </Typography>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0px 2px 8px 0px #00000014',
          overflow: 'hidden',
        }}
      >
        {items.map((item, index) => {
          const isOutOfStock = enforceStock && item.remaining <= 0
          return (
            <Box
              key={item.name}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1.5,
                px: 2,
                borderBottom: index < items.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    fontWeight={700}
                    fontSize={14}
                    color={isOutOfStock ? 'text.disabled' : 'text.primary'}
                  >
                    {item.name}
                  </Typography>
                  {isOutOfStock && (
                    <Chip
                      label={t('settings.stock_enforcement.out_of_stock')}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        color: 'error.main',
                        bgcolor: 'error.lighter',
                      }}
                    />
                  )}
                </Stack>
                {item.lot && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                        mr: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography fontSize={11} color="text.secondary">
                        {t('settings.display.lot')}
                      </Typography>
                    </Box>
                    <Typography fontSize={13} color={isOutOfStock ? 'text.disabled' : 'text.secondary'}>
                      {item.lot}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography fontSize={13} color={isOutOfStock ? 'text.disabled' : 'text.secondary'}>
                {t('settings.display.remaining')} {item.remaining} {item.unit}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default InventoryStockEnforcementPage
